import { type FilterFunction } from "types/types";

import { VALID_REGEX } from "../../util/constants";
import { stringifyParams } from "../../util/stringifyParams";
import Singleton from "../Singleton";

import { ContentServiceClientError } from "./Error";
import {
  type ContentServiceClientParams,
  type ContentServiceErrorResponse,
  type ContentServiceGetStorySearchParams,
  type ContentServicePostStorySearchParams,
  type ContentServiceStorySearchResponse,
  type ContentServiceStorySlugParamsStringified,
} from "./types";

export default class ContentServiceClient extends Singleton {
  host: string;
  #username: string;
  #password: string;

  constructor(params: ContentServiceClientParams) {
    super();

    if (!params.host) {
      throw new Error(
        `No host passed to ContentServiceClient constructor; aborting`
      );
    }

    if (!params.username) {
      throw new Error(
        `No username passed to ContentServiceClient constructor; aborting`
      );
    }

    if (!params.password) {
      throw new Error(
        `No password passed to ContentServiceClient constructor; aborting`
      );
    }

    this.host = params.host;
    this.#username = params.username;
    this.#password = params.password;
  }

  getAuthHeader() {
    return {
      Authorization: `Basic ${btoa(`${this.#username}:${this.#password}`)}`,
    };
  }

  getV1StoriesSearchBy: FilterFunction<ContentServiceStorySearchResponse> =
    async (
      params: ContentServiceGetStorySearchParams
    ): Promise<ContentServiceStorySearchResponse> => {
      this.validateGetV1StoriesSearchBy(params);

      const urlParams = stringifyParams(params);
      const url = `${this.host}/v1/search?${urlParams}`;

      const response = await fetch(url, {
        headers: this.getAuthHeader(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new ContentServiceClientError({
          errorResponse: data as ContentServiceErrorResponse,
          status: response.status,
          url,
        });
      }

      return data;
    };

  getV1StoriesBySlug = async (
    _params: ContentServiceStorySlugParamsStringified
  ) => {
    // Implement method to return story by slug
  };

  postV1StoriesSearchBy: FilterFunction<ContentServiceStorySearchResponse> =
    async (
      params: ContentServicePostStorySearchParams
    ): Promise<ContentServiceStorySearchResponse> => {
      this.validatePostV1StoriesSearchBy(params);

      const url = `${this.host}/v1/search`;

      const response = await fetch(url, {
        method: "POST",
        headers: this.getAuthHeader(),
        body: JSON.stringify({
          filter: { params },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new ContentServiceClientError({
          errorResponse: data as ContentServiceErrorResponse,
          status: response.status,
          url,
        });
      }

      return data;
    };

  validateGetV1StoriesSearchBy = (
    params: ContentServicePostStorySearchParams
  ): boolean => {
    const lengthLimit = 200;

    Object.entries(params).forEach(([key, value]) => {
      if (key.length > lengthLimit) {
        throw new Error(
          `Invalid key passed to ContentServiceClient.getV1StoriesSearchBy: ${key} exceeds length limit`
        );
      }

      if (!key.toString().match(VALID_REGEX.objectKey)) {
        throw new Error(
          `Invalid key passed to ContentServiceClient.getV1StoriesSearchBy: ${key}`
        );
      }

      if (value.toString().length > lengthLimit) {
        throw new Error(
          `Invalid value passed to ContentServiceClient.getV1StoriesSearchBy: ${value} exceeds length limit`
        );
      }

      if (!value.toString().match(VALID_REGEX.objectValue)) {
        throw new Error(
          `Invalid value passed to ContentServiceClient.getV1StoriesSearchBy with ${key}: ${value}`
        );
      }
    });

    return true;
  };

  validatePostV1StoriesSearchBy = (
    params: ContentServicePostStorySearchParams
  ): boolean => {
    const lengthLimit = 200;

    Object.entries(params).forEach(([key, value]) => {
      if (key.length > lengthLimit) {
        throw new Error(
          `Invalid key passed to ContentServiceClient.postV1StoriesSearchBy: ${key} exceeds length limit`
        );
      }

      if (!key.toString().match(VALID_REGEX.objectKey)) {
        throw new Error(
          `Invalid key passed to ContentServiceClient.postV1StoriesSearchBy: ${key}`
        );
      }

      if (typeof value === "object" && !value) {
        throw new Error(
          `Invalid value passed to ContentServiceClient.postV1StoriesSearchBy with ${key}: ${value}`
        );
      }

      if (typeof value !== "object") {
        if (value && value.toString().length > lengthLimit) {
          throw new Error(
            `Invalid value passed to ContentServiceClient.postV1StoriesSearchBy: ${value} exceeds length limit`
          );
        }

        if (value && !value.toString().match(VALID_REGEX.objectValue)) {
          throw new Error(
            `Invalid value passed to ContentServiceClient.postV1StoriesSearchBy with ${key}: ${value}`
          );
        }
      }
    });

    return true;
  };
}
